import React from "react";
import { HStack } from "@design-system/HStack/HStack";
import { InstitutionIcon } from "@design-system/InstitutionIcon/InstitutionIcon";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { PlaidConnectionWithAccounts } from "@common/types/plaidConnection";
import { PLAID_PAYPAL_INSTITUTION_ID } from "@common/helpers/plaid";
import { useLatestTransactionsSyncTime } from "@hooks/useLatestSyncTime";
import { timeAgoInSentence } from "@helpers/date";
import { AlertIcon, ClockIcon } from "@icons";
import { BrokenPlaidConnectionLink } from "../BrokenPlaidConnectionLink";
import { useManualDataUpload } from "./useManualDataUpload";

interface Props {
    connection: PlaidConnectionWithAccounts;
}
export const DisplayPlaidConnectionHeader: React.FC<Props> = ({
    connection,
}) => {
    const { isMissingData } = useManualDataUpload(connection);
    const hasAccountsWithMissingInfo = connection.accounts.some(
        (acc) => !acc.financialAccount?.entityId,
    );
    const lastSynced = useLatestTransactionsSyncTime(connection.accounts ?? []);

    const isLegacyIntegration =
        connection.institutionId === PLAID_PAYPAL_INSTITUTION_ID;

    return (
        <HStack gap={24} align="center">
            <InstitutionIcon src={connection.bankLogoUrl} size="lg" />

            <div className="plaid-connection__description">
                <h5>
                    {connection.bankName}
                    {isLegacyIntegration && " (Legacy)"}{" "}
                    {hasAccountsWithMissingInfo && (
                        <Tooltip content="One or more accounts have missing information.">
                            <span>
                                <AlertIcon className="ml-2 icon-color-red-600" />
                            </span>
                        </Tooltip>
                    )}
                </h5>
                <HStack as="span" align="center">
                    {!connection.error && lastSynced && (
                        <>
                            <ClockIcon
                                className="icon-color-gray-900 mr-1"
                                height={12}
                            />
                            Transactions synced {timeAgoInSentence(lastSynced)}{" "}
                            by Plaid
                        </>
                    )}
                    {connection.error && (
                        <BrokenPlaidConnectionLink connection={connection} />
                    )}

                    {isMissingData && (
                        <span className="plaid-connection__missing-data">
                            <AlertIcon className="mr-2 icon-color-current" />
                            Missing Data
                        </span>
                    )}
                </HStack>
            </div>
        </HStack>
    );
};
